import Calendar, { type ChangeEvent as CalendarChangeEvent } from '@atlaskit/calendar';
import { DateTimePicker } from '@atlaskit/datetime-picker';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import Portal from '@atlaskit/portal';
import { components as selectComponents } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import { TimeFormat } from '@databases/model/field';
import { DatabaseFieldType, type RawDatabaseValue } from '@databases/types/database';
import { type ValueChange } from '@databases/utils/database/changesets';
import {
  getLocalizedDatePlaceholder,
  normalizeDatetimeString,
  parseDate,
  stringToDate
} from '@databases/utils/database/field-types/date';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { type IntlShape, useIntl } from 'react-intl-next';

import styles from './date-default-value-picker.module.css';

type DateDefaultValuePickerProps = {
  ariaDescribedBy?: string;
  ariaLabel?: string;
  id?: string;
  onBlur?: () => void;
  onChange: (
    value: RawDatabaseValue<DatabaseFieldType.DATE>,
    change: ValueChange | null
  ) => void;
  onFocus?: () => void;
  timeFormat?: TimeFormat;
  value: RawDatabaseValue<DatabaseFieldType.DATE>;
};

const PICKER_HEIGHT = 32;
const PICKER_POPUP_OFFSET = 2;
const PICKER_Z_INDEX = 1000;
const DATE_TIME_PICKER_TEST_ID = 'date-default-value-datetime-picker';

type PickerPosition = {
  top: number;
  left: number;
  width: number;
};

type DateDefaultValuePickerContentProps = DateDefaultValuePickerProps & {
  formatMessage: IntlShape['formatMessage'];
  locale: string;
};

const EmptySelectIndicator = () => null;

const getDateOnlyParts = (date: string | undefined) =>
  date?.match(/^(\d{4})-(\d{2})-(\d{2})$/);

const getDateParts = (date: string | undefined) => {
  const parts = getDateOnlyParts(date);

  return parts
    ? {
        day: Number(parts[3]),
        month: Number(parts[2]),
        year: Number(parts[1])
      }
    : undefined;
};

const getCalendarDate = (date: string | undefined) =>
  parseDate(date)?.slice(0, 10) ?? getDateOnlyParts(date?.slice(0, 10))?.[0];

const getDisplayDate = (date: string | undefined, locale: string) => {
  const parsedDate = parseDate(date);
  const parts = getDateParts(parsedDate?.slice(0, 10));

  if (!parts) {
    return '';
  }

  return new Intl.DateTimeFormat(locale, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(parts.year, parts.month - 1, parts.day));
};

const getCalendarDateParts = (inputValue: string, selectedDate: string | undefined) => {
  const selectedDateParts = getDateParts(selectedDate);
  const parsedInputParts = getDateParts(stringToDate(inputValue));
  const hasExplicitYear = /\d{4}/.test(inputValue);

  if (parsedInputParts && hasExplicitYear) {
    return parsedInputParts;
  }

  const partialMonth = inputValue.trim().match(/^(\d{1,2})(?:\D|$)/);
  const partialMonthNumber = partialMonth ? Number(partialMonth[1]) : undefined;

  if (
    partialMonthNumber !== undefined &&
    partialMonthNumber >= 1 &&
    partialMonthNumber <= 12
  ) {
    return {
      day: 0,
      month: partialMonthNumber,
      year: selectedDateParts?.year ?? new Date().getFullYear()
    };
  }

  return parsedInputParts ?? selectedDateParts;
};

const DateDefaultValuePicker: FunctionComponent<DateDefaultValuePickerProps> = ({
  value,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const locale = useLocale();

  return (
    <DateDefaultValuePickerContent
      key={`${locale}:${value ?? ''}`}
      {...props}
      value={value}
      formatMessage={formatMessage}
      locale={locale}
    />
  );
};

const DateDefaultValuePickerContent: FunctionComponent<
  DateDefaultValuePickerContentProps
> = ({
  ariaLabel,
  formatMessage,
  locale,
  onBlur,
  onChange,
  onFocus,
  ariaDescribedBy,
  id,
  timeFormat,
  value
}) => {
  const triggerRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const dateTimePickerRef = useRef<HTMLDivElement>(null);
  const dateTimeCalendarRef = useRef<HTMLDivElement>(null);
  const shouldCommitOnBlurRef = useRef(true);
  const shouldSuppressDateTimeDateClickRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState(() => getDisplayDate(value, locale));
  const [calendarDateParts, setCalendarDateParts] = useState(() =>
    getCalendarDateParts(getDisplayDate(value, locale), parseDate(value)?.slice(0, 10))
  );
  const [calendarPosition, setCalendarPosition] = useState<{
    top: number;
    left: number;
    width: number;
  }>();
  const [isDateTimeFocused, setIsDateTimeFocused] = useState(false);
  const [isDateTimeDatePickerOpen, setIsDateTimeDatePickerOpen] = useState(false);
  const [dateTimePickerMenuPosition, setDateTimePickerMenuPosition] =
    useState<PickerPosition>();
  const includeTime = Boolean(timeFormat);
  const is12Hour = timeFormat === TimeFormat.TWELVE_HOUR;
  const dateTimeSelectStyles = useMemo(
    () => ({
      control: (base: any) => ({
        ...base,
        background: 'transparent',
        border: 0,
        borderRadius: 0,
        boxShadow: 'none',
        minHeight: 30,
        ':hover': {
          background: 'transparent',
          border: 0,
          cursor: 'inherit'
        }
      }),
      menu: (base: any) => ({
        ...base,
        margin: 0,
        position: 'static',
        zIndex: PICKER_Z_INDEX
      }),
      valueContainer: (base: any) => ({
        ...base,
        paddingBottom: 0,
        paddingTop: 0
      })
    }),
    []
  );
  const DateTimeCalendarMenu = useMemo(() => {
    function DateTimeCalendarMenuComponent({ innerProps, selectProps }: any) {
      const selectedDate = getCalendarDate(selectProps.calendarValue);
      const calendarDate = getCalendarDate(
        selectProps.calendarView ?? selectProps.calendarValue
      );
      const dateParts = getDateParts(calendarDate ?? selectedDate);

      return (
        <Portal zIndex={PICKER_Z_INDEX}>
          <div
            {...innerProps}
            ref={dateTimeCalendarRef}
            data-contextual-menu="true"
            data-testid={`${DATE_TIME_PICKER_TEST_ID}--datepicker--menu-positioner`}
            onMouseDownCapture={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            style={{
              background: token('elevation.surface.overlay', '#FFFFFF'),
              borderRadius: token('radius.medium'),
              boxShadow: token(
                'elevation.shadow.overlay',
                '0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)'
              ),
              // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
              left: dateTimePickerMenuPosition?.left,
              minWidth: dateTimePickerMenuPosition?.width,
              overflow: 'hidden',
              position: 'fixed',
              // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
              top: dateTimePickerMenuPosition?.top,
              width: 'max-content',
              zIndex: PICKER_Z_INDEX
            }}
          >
            <Calendar
              className={styles.calendar}
              locale={selectProps.calendarLocale}
              day={dateParts?.day ?? 0}
              month={dateParts?.month}
              year={dateParts?.year}
              disabled={selectProps.calendarDisabled}
              disabledDateFilter={selectProps.calendarDisabledDateFilter}
              minDate={selectProps.calendarMinDate}
              maxDate={selectProps.calendarMaxDate}
              nextMonthLabel={selectProps.nextMonthLabel}
              onChange={selectProps.onCalendarChange}
              onSelect={selectProps.onCalendarSelect}
              previousMonthLabel={selectProps.previousMonthLabel}
              selected={selectedDate ? [selectedDate] : []}
              shouldSetFocusOnCurrentDay={selectProps.shouldSetFocusOnCurrentDay}
              testId={selectProps.testId ? `${selectProps.testId}--calendar` : undefined}
              weekStartDay={selectProps.calendarWeekStartDay}
            />
          </div>
        </Portal>
      );
    }

    return DateTimeCalendarMenuComponent;
  }, [dateTimePickerMenuPosition]);
  const DateTimeTimeMenu = useMemo(() => {
    function DateTimeTimeMenuComponent({ children, ...props }: any) {
      return (
        <Portal zIndex={PICKER_Z_INDEX}>
          <div
            data-testid={`${DATE_TIME_PICKER_TEST_ID}--timepicker--menu-positioner`}
            style={{
              // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
              left: dateTimePickerMenuPosition?.left,
              position: 'fixed',
              // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
              top: dateTimePickerMenuPosition?.top,
              width: dateTimePickerMenuPosition?.width,
              zIndex: PICKER_Z_INDEX
            }}
          >
            <selectComponents.Menu {...props}>{children}</selectComponents.Menu>
          </div>
        </Portal>
      );
    }

    return DateTimeTimeMenuComponent;
  }, [dateTimePickerMenuPosition]);

  const dateSelectProps = useMemo(
    () => ({
      components: {
        ClearIndicator: EmptySelectIndicator,
        DropdownIndicator: EmptySelectIndicator,
        Menu: DateTimeCalendarMenu
      },
      styles: dateTimeSelectStyles
    }),
    [DateTimeCalendarMenu, dateTimeSelectStyles]
  );
  const timeSelectProps = useMemo(
    () => ({
      components: {
        ClearIndicator: EmptySelectIndicator,
        DropdownIndicator: EmptySelectIndicator,
        Menu: DateTimeTimeMenu
      },
      styles: dateTimeSelectStyles
    }),
    [DateTimeTimeMenu, dateTimeSelectStyles]
  );

  useEffect(() => {
    if (!isDateTimeDatePickerOpen) {
      return;
    }

    const stopPortaledDateTimeCalendarMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && dateTimeCalendarRef.current?.contains(target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const stopPortaledDateTimeCalendarChromeClick = (event: MouseEvent) => {
      const target = event.target;
      if (
        !(target instanceof Element) ||
        !dateTimeCalendarRef.current?.contains(target)
      ) {
        return;
      }

      if (target.closest('button')) {
        return;
      }

      event.stopPropagation();
    };

    window.addEventListener('mousedown', stopPortaledDateTimeCalendarMouseDown, true);
    window.addEventListener('click', stopPortaledDateTimeCalendarChromeClick, true);

    return () => {
      window.removeEventListener(
        'mousedown',
        stopPortaledDateTimeCalendarMouseDown,
        true
      );
      window.removeEventListener('click', stopPortaledDateTimeCalendarChromeClick, true);
    };
  }, [isDateTimeDatePickerOpen]);

  const handleChange = useCallback(
    (nextValue: RawDatabaseValue<DatabaseFieldType.DATE>) => {
      onChange(nextValue, {
        type: DatabaseFieldType.DATE,
        value: nextValue
      });
    },
    [onChange]
  );

  const handleDateTimeChange = useCallback(
    (nextValue: string) => {
      let normalizedValue = nextValue || undefined;
      if (normalizedValue?.includes('T')) {
        normalizedValue = normalizeDatetimeString(normalizedValue);
      }

      handleChange(normalizedValue);
    },
    [handleChange]
  );
  const handleDateTimeFocus = useCallback(
    (_event: React.FocusEvent<HTMLInputElement>) => {
      setIsDateTimeFocused(true);
      onFocus?.();
    },
    [onFocus]
  );
  const handleDateTimeBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const nextFocusedElement = event.relatedTarget;
      if (
        nextFocusedElement instanceof Node &&
        dateTimeCalendarRef.current?.contains(nextFocusedElement)
      ) {
        return;
      }

      setIsDateTimeFocused(false);
      setIsDateTimeDatePickerOpen(false);
      onBlur?.();
    },
    [onBlur]
  );
  const updateDateTimePickerMenuPosition = useCallback(
    (target: Element, picker: 'date' | 'time') => {
      const valueContainerRect = dateTimePickerRef.current?.getBoundingClientRect();
      if (!valueContainerRect) {
        return;
      }

      const pickerContainerRect =
        picker === 'time'
          ? target
              .closest(
                `[data-testid="${DATE_TIME_PICKER_TEST_ID}--timepicker--container"]`
              )
              ?.getBoundingClientRect()
          : valueContainerRect;
      const anchorRect = pickerContainerRect ?? valueContainerRect;

      setDateTimePickerMenuPosition({
        top: valueContainerRect.bottom + PICKER_POPUP_OFFSET,
        left: anchorRect.left,
        width: anchorRect.width
      });
    },
    []
  );
  const handleDateTimeDateFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const target = event.target;
      if (target instanceof Element) {
        updateDateTimePickerMenuPosition(target, 'date');
      }

      setIsDateTimeDatePickerOpen(true);
      handleDateTimeFocus(event);
    },
    [handleDateTimeFocus, updateDateTimePickerMenuPosition]
  );
  const handleDateTimeTimeFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const target = event.target;
      if (target instanceof Element) {
        updateDateTimePickerMenuPosition(target, 'time');
      }

      handleDateTimeFocus(event);
    },
    [handleDateTimeFocus, updateDateTimePickerMenuPosition]
  );
  const handleDateTimeContainerMouseDownCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const datePickerContainer = target.closest(
        `[data-testid="${DATE_TIME_PICKER_TEST_ID}--datepicker--container"]`
      );
      const timePickerContainer = target.closest(
        `[data-testid="${DATE_TIME_PICKER_TEST_ID}--timepicker--container"]`
      );
      if (datePickerContainer) {
        updateDateTimePickerMenuPosition(target, 'date');
      } else if (timePickerContainer) {
        updateDateTimePickerMenuPosition(target, 'time');
      }

      if (!datePickerContainer) {
        return;
      }

      if (!isDateTimeDatePickerOpen) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      shouldSuppressDateTimeDateClickRef.current = true;
      setIsDateTimeDatePickerOpen(false);

      const activeElement = globalThis.document?.activeElement;
      if (
        activeElement instanceof HTMLElement &&
        datePickerContainer.contains(activeElement)
      ) {
        activeElement.blur();
      }
    },
    [isDateTimeDatePickerOpen, updateDateTimePickerMenuPosition]
  );
  const handleDateTimeContainerClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!shouldSuppressDateTimeDateClickRef.current) {
        return;
      }

      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          `[data-testid="${DATE_TIME_PICKER_TEST_ID}--datepicker--container"]`
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }

      shouldSuppressDateTimeDateClickRef.current = false;
    },
    []
  );
  const datePickerProps = useMemo(
    () => ({
      onFocus: handleDateTimeDateFocus,
      placeholder: getLocalizedDatePlaceholder(locale)
    }),
    [handleDateTimeDateFocus, locale]
  );
  const timePickerProps = useMemo(
    () => ({
      onFocus: handleDateTimeTimeFocus,
      timeFormat: is12Hour ? 'h:mm a' : 'HH:mm'
    }),
    [handleDateTimeTimeFocus, is12Hour]
  );
  const dateTimePickerInnerProps = useMemo<
    React.HTMLAttributes<HTMLDivElement> & {
      ref: React.RefObject<HTMLDivElement>;
    }
  >(
    () => ({
      ref: dateTimePickerRef,
      style: {
        background: token('color.background.input'),
        borderColor: isDateTimeFocused
          ? token('color.border.focused', '#388BFF')
          : token('color.border'),
        borderStyle: 'solid',
        borderWidth: token('border.width'),
        borderRadius: token('radius.medium'),
        boxSizing: 'border-box',
        boxShadow: 'none',
        height: PICKER_HEIGHT
      },
      onClickCapture: handleDateTimeContainerClickCapture,
      onMouseDownCapture: handleDateTimeContainerMouseDownCapture
    }),
    [
      handleDateTimeContainerClickCapture,
      handleDateTimeContainerMouseDownCapture,
      isDateTimeFocused
    ]
  );

  const updateCalendarPosition = useCallback(() => {
    const triggerRect = triggerRef.current?.getBoundingClientRect();

    if (triggerRect) {
      setCalendarPosition({
        top: triggerRect.bottom + PICKER_POPUP_OFFSET,
        left: triggerRect.left,
        width: triggerRect.width
      });
    }
  }, []);

  const openCalendar = useCallback(() => {
    triggerRef.current?.focus();
    updateCalendarPosition();
    setIsOpen(true);
  }, [updateCalendarPosition]);

  const handleTriggerMouseDown = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      event.stopPropagation();

      if (isOpen) {
        setIsOpen(false);
        onBlur?.();
        return;
      }

      openCalendar();
    },
    [isOpen, onBlur, openCalendar]
  );

  const commitInputValue = useCallback(() => {
    const parsedValue = stringToDate(inputValue);
    const currentValue = parseDate(value)?.slice(0, 10);

    if (parsedValue !== currentValue) {
      handleChange(parsedValue);
    }

    setInputValue(parsedValue ? getDisplayDate(parsedValue, locale) : '');
  }, [handleChange, inputValue, locale, value]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextInputValue = event.currentTarget.value;
      setInputValue(nextInputValue);
      setCalendarDateParts(
        getCalendarDateParts(nextInputValue, parseDate(value)?.slice(0, 10))
      );
    },
    [value]
  );

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
    onFocus?.();
  }, [onFocus]);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);

    if (shouldCommitOnBlurRef.current) {
      commitInputValue();
    } else {
      shouldCommitOnBlurRef.current = true;
    }

    onBlur?.();
  }, [commitInputValue, onBlur]);

  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
        event.currentTarget.blur();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        shouldCommitOnBlurRef.current = false;
        setInputValue(getDisplayDate(value, locale));
        setIsOpen(false);
        event.currentTarget.blur();
        return;
      }

      if (event.key !== 'ArrowDown') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      openCalendar();
    },
    [locale, openCalendar, value]
  );

  const handleCalendarChange = useCallback((date: CalendarChangeEvent) => {
    setCalendarDateParts({
      day: date.day,
      month: date.month,
      year: date.year
    });
  }, []);

  const handleDateSelect = useCallback(
    (date: { iso: string }) => {
      handleChange(date.iso);
      setIsOpen(false);
      onBlur?.();
    },
    [handleChange, onBlur]
  );

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      handleChange(undefined);
      setIsOpen(false);
      onBlur?.();
    },
    [handleChange, onBlur]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      shouldCommitOnBlurRef.current = false;
      setInputValue(getDisplayDate(value, locale));
      setIsOpen(false);

      if (triggerRef.current && document.activeElement === triggerRef.current) {
        triggerRef.current.blur();
      } else {
        onBlur?.();
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (triggerRef.current?.contains(target) || calendarRef.current?.contains(target)) {
        return;
      }

      setIsOpen(false);
      onBlur?.();
    };

    window.addEventListener('keydown', handleEscapeKeyDown, true);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('keydown', handleEscapeKeyDown, true);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, locale, onBlur, value]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const stopPortaledCalendarEvent = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && calendarRef.current?.contains(target)) {
        event.stopPropagation();
      }
    };

    const stopPortaledCalendarChromeClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element) || !calendarRef.current?.contains(target)) {
        return;
      }

      if (target.closest('button')) {
        return;
      }

      event.stopPropagation();
    };

    window.addEventListener('mousedown', stopPortaledCalendarEvent, true);
    window.addEventListener('click', stopPortaledCalendarChromeClick, true);

    return () => {
      window.removeEventListener('mousedown', stopPortaledCalendarEvent, true);
      window.removeEventListener('click', stopPortaledCalendarChromeClick, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    window.addEventListener('resize', updateCalendarPosition);
    window.addEventListener('scroll', updateCalendarPosition, true);

    return () => {
      window.removeEventListener('resize', updateCalendarPosition);
      window.removeEventListener('scroll', updateCalendarPosition, true);
    };
  }, [isOpen, updateCalendarPosition]);

  const selectedDate = parseDate(value)?.slice(0, 10);
  const isActive = isInputFocused || isOpen;
  const calendar =
    isOpen && calendarPosition && typeof document !== 'undefined' ? (
      <Portal zIndex={PICKER_Z_INDEX}>
        <div
          ref={calendarRef}
          data-contextual-menu="true"
          data-testid="date-default-value-calendar-popup"
          style={{
            backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
            borderRadius: token('radius.medium'),
            boxShadow: token(
              'elevation.shadow.overlay',
              '0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)'
            ),
            // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
            left: calendarPosition.left,
            minWidth: calendarPosition.width,
            overflow: 'hidden',
            position: 'fixed',
            // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
            top: calendarPosition.top,
            width: 'max-content',
            zIndex: PICKER_Z_INDEX
          }}
        >
          <Calendar
            className={styles.calendar}
            locale={locale}
            day={calendarDateParts?.day ?? 0}
            month={calendarDateParts?.month}
            year={calendarDateParts?.year}
            onChange={handleCalendarChange}
            onSelect={handleDateSelect}
            selected={selectedDate ? [selectedDate] : []}
            testId="date-default-value-calendar"
          />
        </div>
      </Portal>
    ) : null;

  if (includeTime) {
    return (
      <div>
        <DateTimePicker
          value={parseDate(value, true) ?? ''}
          onChange={handleDateTimeChange}
          onFocus={handleDateTimeFocus}
          onBlur={handleDateTimeBlur}
          locale={locale}
          datePickerProps={datePickerProps}
          timePickerProps={timePickerProps}
          datePickerSelectProps={dateSelectProps}
          timePickerSelectProps={timeSelectProps}
          timeIsEditable
          spacing="compact"
          innerProps={dateTimePickerInnerProps}
          testId={DATE_TIME_PICKER_TEST_ID}
          aria-label={ariaLabel ?? formatMessage(messages['field-settings.type-date'])}
          aria-describedby={ariaDescribedBy}
          id={id}
        />
      </div>
    );
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <input
          ref={triggerRef}
          id={id}
          value={inputValue}
          placeholder={getLocalizedDatePlaceholder(locale)}
          aria-label={ariaLabel ?? formatMessage(messages['field-settings.type-date'])}
          aria-describedby={ariaDescribedBy}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onMouseDown={handleTriggerMouseDown}
          onKeyDown={handleTriggerKeyDown}
          data-testid="date-default-value-picker-trigger"
          style={{
            alignItems: 'center',
            backgroundColor: token('color.background.input', '#FFFFFF'),
            borderColor: isActive ? token('color.border.focused') : token('color.border'),
            borderRadius: token('radius.medium'),
            borderStyle: 'solid',
            borderWidth: 1,
            boxShadow: isActive
              ? `inset 0 0 0 1px ${token('color.border.focused', '#388BFF')}`
              : undefined,
            boxSizing: 'border-box',
            color: value
              ? token('color.text', '#172B4D')
              : token('color.text.subtlest', '#626F86'),
            cursor: 'text',
            display: 'flex',
            font: token(
              'font.body',
              'normal 400 14px/20px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            ),
            height: PICKER_HEIGHT,
            outline: 'none',
            paddingInline: token('space.100', '8px'),
            width: '100%'
          }}
        />
        {value && (
          <button
            aria-label={formatMessage(
              messages['field-settings.clear-date-default-value.non-final']
            )}
            onClick={handleClear}
            style={{
              alignItems: 'center',
              background: 'transparent',
              border: 0,
              color: token('color.icon.subtle', '#626F86'),
              cursor: 'pointer',
              display: 'flex',
              height: 24,
              justifyContent: 'center',
              margin: 0,
              padding: 0,
              position: 'absolute',
              right: token('space.075', '6px'),
              // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
              top: '50%',
              transform: 'translateY(-50%)',
              width: 24
            }}
            type="button"
          >
            <CrossCircleIcon label="" size="small" />
          </button>
        )}
      </div>
      {calendar}
    </>
  );
};

export default DateDefaultValuePicker;
